/**
 * ThemePreviewIframe - Wrapper component for theme preview iframe
 *
 * Loads the standalone theme preview app (optidev-theme-preview) and initializes
 * the font cache via postMessage. Used in Theme Library settings page for
 * live theme preview without requiring a project context.
 */

import { useRef, useState, useEffect, useCallback } from "react";

import { Loader2 } from "lucide-react";

import { GOOGLE_FONTS } from "@optidev/shared-types";

import { cn } from "@/lib/utils";
import { previewMessage } from "@/lib/visual-editor/post-message";

interface ThemePreviewIframeProps {
  /** Callback when iframe is ready and fonts are initialized */
  onIframeReady: (iframe: HTMLIFrameElement) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get the theme preview URL from environment variable
 */
function getPreviewUrl(): string {
  const url = import.meta.env.VITE_THEME_PREVIEW_URL;
  if (!url) {
    console.warn("VITE_THEME_PREVIEW_URL is not set");
    return "";
  }
  return url;
}

export function ThemePreviewIframe({
  onIframeReady,
  className,
}: ThemePreviewIframeProps): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const previewUrl = getPreviewUrl();

  // Handle messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Visual editor signals ready
      if (event.data?.type === "visual-editor-ready" && iframeRef.current) {
        setIsLoading(false);
        setHasError(false);

        // Initialize fonts cache in iframe
        previewMessage.initTheme(iframeRef.current, GOOGLE_FONTS);

        // Notify parent that iframe is ready
        onIframeReady(iframeRef.current);
      }
    },
    [onIframeReady]
  );

  // Listen for messages
  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  return (
    <div className={cn("relative w-full h-full", className)}>
      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/50 z-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">
            Loading preview...
          </p>
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/50 z-10">
          <p className="text-sm text-muted-foreground text-center px-4">
            Preview not available.
            <br />
            <span className="text-xs">
              Make sure the theme preview server is running on port 5175.
            </span>
          </p>
        </div>
      )}

      {/* Preview iframe */}
      <iframe
        ref={iframeRef}
        src={previewUrl}
        className="w-full h-full border-0"
        title="Theme Preview"
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
}

export default ThemePreviewIframe;
